import type { ComponentType } from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Facebook, Instagram, Linkedin, Youtube } from 'lucide-react';
import { Separator } from '@/components/ui/separator';
import { useSiteConfig } from '@/context/SiteConfigContext';
import { Logo } from '@/components/common/Logo';

const quickLinks = [
  { to: '/centres', label: 'Find a Clinic' },
  { to: '/knowledge', label: 'Knowledge' },
  { to: '/partners', label: 'For Partners' },
  { to: '/partner/login', label: 'Partner Login' },
  { to: '/contact', label: 'Contact Us' },
];

// Legal / Terms & Conditions documents (anchored sections on the Legal page).
const legalLinks = [
  { to: '/legal#terms', label: 'Terms & Conditions' },
  { to: '/legal#privacy', label: 'Privacy Policy' },
  { to: '/legal#refund', label: 'Refund & Cancellation' },
  { to: '/legal#clinic-terms', label: 'Clinic Partner Terms' },
  { to: '/legal#clinic-privacy', label: 'Clinic Privacy Policy' },
];

/** Pinterest has no Lucide equivalent, so the official mark is inlined. It is a
 *  filled glyph next to Lucide's outlines, which is the usual compromise. */
function Pinterest({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" className={className}>
      <path d="M12.017 0C5.396 0 .029 5.367.029 11.987c0 5.079 3.158 9.417 7.618 11.162-.105-.949-.199-2.403.041-3.439.219-.937 1.406-5.957 1.406-5.957s-.359-.72-.359-1.781c0-1.663.967-2.911 2.168-2.911 1.024 0 1.518.769 1.518 1.688 0 1.029-.653 2.567-.992 3.992-.285 1.193.6 2.165 1.775 2.165 2.128 0 3.768-2.245 3.768-5.487 0-2.861-2.063-4.869-5.008-4.869-3.41 0-5.409 2.562-5.409 5.199 0 1.033.394 2.143.889 2.741.099.12.112.225.085.345-.09.375-.293 1.199-.334 1.363-.053.225-.172.271-.401.165-1.495-.69-2.433-2.878-2.433-4.646 0-3.776 2.748-7.252 7.92-7.252 4.158 0 7.392 2.967 7.392 6.923 0 4.135-2.607 7.462-6.233 7.462-1.214 0-2.354-.629-2.758-1.379l-.749 2.848c-.269 1.045-1.004 2.352-1.498 3.146 1.123.345 2.306.535 3.55.535 6.607 0 11.985-5.365 11.985-11.987C23.97 5.39 18.592.026 11.985.026L12.017 0z" />
    </svg>
  );
}

const socialIconMap: Record<string, ComponentType<{ className?: string }>> = {
  facebook: Facebook,
  instagram: Instagram,
  linkedin: Linkedin,
  pinterest: Pinterest,
  youtube: Youtube,
};

/** Capitalising the config key alone would give "Linkedin" and "Youtube". */
const socialLabels: Record<string, string> = {
  linkedin: 'LinkedIn',
  youtube: 'YouTube',
};

export function Footer() {
  const config = useSiteConfig();
  const { contact, social_links, hero } = config;

  // A key with no icon used to fall back to Facebook's, so an unrecognised
  // network rendered as the wrong brand. Placeholder "#" links are dropped too.
  // Values are typed into Mongo by hand, so a stray space is a matter of time.
  const socialLinks = Object.entries(social_links || {})
    .map(([key, href]) => [key, String(href ?? '').trim()] as const)
    .filter(([key, href]) => socialIconMap[key] && href && href !== '#')
    .map(([key, href]) => ({
      icon: socialIconMap[key],
      label: socialLabels[key] || key.charAt(0).toUpperCase() + key.slice(1),
      href,
    }));

  return (
    <footer className="border-t bg-card" role="contentinfo">
      <div className="mx-auto max-w-[1440px] px-5 py-14 sm:px-8 lg:px-12">
        <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-5">
          {/* Column 1: Brand */}
          <div className="space-y-3 lg:col-span-2">
            <Link to="/" aria-label="Recovry home" className="flex items-center">
              <Logo className="h-8" />
            </Link>
            <p className="text-xs font-medium text-primary/80">
              {hero.tagline}
            </p>
            <p className="max-w-md text-sm leading-relaxed text-muted-foreground">
              Recovry connects you with verified physiotherapy clinics across PAN India.
              Browse by symptom or service, compare clinics, read real patient reviews, and
              book your session in minutes.
            </p>
          </div>

          {/* Column 2: Quick Links */}
          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-foreground">Quick Links</h3>
            <ul className="space-y-2">
              {quickLinks.map(({ to, label }) => (
                <li key={label}>
                  <Link
                    to={to}
                    className="text-sm text-muted-foreground transition-colors hover:text-primary"
                  >
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 3: Legal */}
          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-foreground">Legal</h3>
            <ul className="space-y-2">
              {legalLinks.map(({ to, label }) => (
                <li key={label}>
                  <Link
                    to={to}
                    className="text-sm text-muted-foreground transition-colors hover:text-primary"
                  >
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 4: Contact + Social */}
          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-foreground">Contact</h3>
            <ul className="space-y-3">
              <li className="flex items-start gap-2">
                <Phone className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                <a
                  href={`tel:${contact.phone}`}
                  className="text-sm text-muted-foreground transition-colors hover:text-primary"
                >
                  {contact.phone}
                </a>
              </li>
              <li className="flex items-start gap-2">
                <Mail className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                <a
                  href={`mailto:${contact.email}`}
                  className="text-sm text-muted-foreground transition-colors hover:text-primary"
                >
                  {contact.email}
                </a>
              </li>
              <li className="flex items-start gap-2">
                <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                <span className="text-sm text-muted-foreground">{contact.address}</span>
              </li>
            </ul>
            <div className="flex items-center gap-3 pt-1">
              {socialLinks.map(({ icon: Icon, label, href }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  className="flex h-9 w-9 items-center justify-center rounded-full bg-secondary text-primary transition-colors hover:bg-primary hover:text-primary-foreground"
                >
                  <Icon className="h-4 w-4" />
                </a>
              ))}
            </div>
          </div>
        </div>

        <Separator className="my-8" />

        <div className="flex flex-col items-center justify-between gap-3 text-sm text-muted-foreground sm:flex-row">
          <p>&copy; {new Date().getFullYear()} Recovry. All rights reserved.</p>
          <p className="text-xs">Verified clinics only. Free to browse. Hindi &amp; English support.</p>
        </div>
      </div>
    </footer>
  );
}
